/**
 * @Description 配置了商圈地图拓店任务时，地址组件详情地图
 *
 * 需要读取匹配商圈属性的 templateRestriction
"{\"modelId\":18,\"modelCode\":\"kwk_milk_tea_model_000\",\"modelName\":\"卡旺卡\"}"
 *
 */

import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMethods } from '@lhb/hook';
import { isArray, isNotEmpty, isNotEmptyAny } from '@lhb/func';
import cs from 'classnames';
import styles from './index.module.less';
import AMap from '@/common/components/AMap/index';
import IconFont from '@/common/components/IconFont';
import { clusterAndShopsAndChancePoints } from '@/common/api/expandStore/expansionCircleTask';
import { ControlType } from '@/common/components/business/DynamicComponent/config';
import { getPCDInfo } from '@/common/utils/map';
import { getConflictList } from '@/common/api/areaProtect';
import MapOfCIrcleAddress, { mapContainerAddress } from '@/common/components/business/DynamicComponent/components/Address/MapOfCIrcleAddress';
import { MapThemeEnum } from '@/layout/ts-config';
import UserInfoContext from '@/layout/context';

const SurroundDistance = 500; // 周边商圈查询的半径
const ZoomLevel = 16;
const ZoomBoundary = 14; // 当级别小于ZoomBoundary时，接口通过半径请求。当级别大于等于ZoomBoundary时，接口通过边界请求。

const AddressClusterMap: FC<any> = ({
  lng,
  lat,
  allComponents,
  disabled = false
}) => {
  const { mapConfig } = useContext(UserInfoContext);
  const [amapIns, setAmapIns] = useState<any>(null); // 地图实例
  const [clusterList, setClusterList] = useState<any>([]);// 商圈数据
  const [shopList, setShopList] = useState<any>([]);// 门店数据
  const [chancePoint, setChancePoint] = useState<any>([]);// 机会点数据
  const [protectRadius, setProtectRadius] = useState<any>(0);// 当前机会点的保护半径

  const centerMarkerRef: any = useRef<any>(null);
  const centerCircleMarkerRef: any = useRef<any>(null);

  useEffect(() => {
    if (!isNotEmpty(lng) || !isNotEmpty(lat)) return;
    updateBusinessCircle(lng, lat);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lng, lat]);

  /**
   * @description 匹配商圈组件中设置的选址地图模型id
   */
  const modelId = useMemo(() => {
    if (!isArray(allComponents)) {
      return null;
    }
    // 商圈匹配字段
    const targetCom = allComponents.find((item: any) => item.controlType === ControlType.MATCH_BUSINESS_CIRCLE.value);
    if (!targetCom || !isNotEmptyAny(targetCom?.restriction)) {
      return null;
    }
    const restriction = JSON.parse(targetCom?.restriction);
    return restriction?.modelId;
  }, [allComponents]);

  useEffect(() => {
    if (!amapIns) return;
    amapIns.on('click', setCenter);
    return () => {
      amapIns.off('click', setCenter);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amapIns]);

  const {
    loadedMapHandle,
    addCenterMarker,
    setCenter,
    updateBusinessCircle,
    handleData,
    handleProtectRadius
  } = useMethods({
    // 地图加载完毕
    loadedMapHandle(mapIns: any) {
      if (!mapIns) return;
      setAmapIns(mapIns);
      if (!isNotEmpty(lng) || !isNotEmpty(lat)) return;
      addCenterMarker(lng, lat, mapIns);
      // 地图加载完时，此时之前组件初始值已经变动过了，作为补充这个时间点需要请求商圈和门店信息
      updateBusinessCircle(lng, lat);
    },
    // 添加详细地址的marker
    addCenterMarker(lng, lat, tmpMapIns) {
      // 更新位置
      if (centerMarkerRef.current) {
        // centerMarkerRef.current.setPosition([lng, lat]);
        // amapIns.setFitView(centerMarkerRef.current);
        return;
      }
      const w = 28.0;// 原图宽高:164-194
      const h = 33.1;
      const customIcon = new window.AMap.Icon({
        // 图标尺寸
        size: new window.AMap.Size(w, h),
        // 图标的取图地址
        image: `https://staticres.linhuiba.com/project-custom/store-assistant-h5/shop_location@2x.png`,
        // 图标所用图片大小
        imageSize: new window.AMap.Size(w, h),
        // 图标取图偏移量
        // imageOffset: new AMap.Pixel(0, 10)
      });

      const marker = new window.AMap.Marker({
        position: new window.AMap.LngLat(lng, lat),
        // 将一张图片的地址设置为 icon
        icon: customIcon,
        // 设置了 icon 以后，设置 icon 的偏移量，以 icon 的 [center bottom] 为原点
        offset: new window.AMap.Pixel(-w / 2, -h),
        zIndex: 21,
      });
      tmpMapIns.add(marker);
      tmpMapIns.setFitView(
        marker,
        false, // 动画过渡到制定位置
        [60, 60, 60, 60], // 周围边距，上、下、左、右
        ZoomLevel, // 最大 zoom 级别
      );
      centerMarkerRef.current = marker;
    },

    // 点击地图设置中心点
    async setCenter(e: any) {
      if (disabled) return;
      const { lnglat } = e;
      amapIns.setCenter(lnglat);
    },

    async updateBusinessCircle(lng, lat) {
      if (!amapIns) return;
      const { southWest, northEast } = amapIns.getBounds(); // 获取地图可视区域的坐标
      const zoom = amapIns.getZoom();
      let params:any = {
        lng,
        lat,
        modelConfigId: modelId,
      };
      // 当级别小于ZoomBoundary时，接口通过半径请求。当级别大于等于ZoomBoundary时，接口通过边界请求。
      params = zoom > ZoomBoundary ? {
        ...params,
        minLat: southWest.lat,
        maxLat: northEast.lat,
        minLng: southWest.lng,
        maxLng: northEast.lng,
      } : {
        ...params,
        distance: SurroundDistance,
      };
      const curViewPortData = await clusterAndShopsAndChancePoints(params);
      const { provinceId, cityId, districtId }:any = await getPCDInfo([lng, lat]);
      const curPointRangeData = await getConflictList({
        lng, lat, provinceId, cityId, districtId
      });

      // 如果存在保护半径，则将protectRadius设置到protectRadiusRef
      if (curPointRangeData?.protectRadius && curPointRangeData?.protectRadius !== protectRadius) {
        setProtectRadius(curPointRangeData?.protectRadius);
      }
      handleData(curViewPortData, curPointRangeData);
    },
    handleData(curViewPortData, curPointRangeData) {
      const { clusterList, shopList, chancePointList } = curViewPortData || {};
      const { conflictRegionItemResponseList } = curPointRangeData || [];
      // 去重机会点和门店数据
      const chancePoint = () => {
        // 冲突区域类型 1 门店 2 机会点
        const res = conflictRegionItemResponseList?.filter((item) => item.regionType === 2);
        const ids = res?.map((item) => item.id);
        chancePointList.forEach((cur) => {
          if (!ids?.includes(cur.id)) {
            res?.push(cur);
          }
        });
        return res;
      };
      const storeData = () => {
        // 冲突区域类型 1 门店 2 机会点
        const res = conflictRegionItemResponseList?.reduce((acc, item) => {
          if (item.regionType === 1) {
            acc.push({
              ...item,
              logo: item.brandLogo,
              statusName: item.shopStatus,
              name: item.regionName,
            });
          }
          return acc;
        }, []);
        const ids = res?.map((item) => item.id) || [];
        shopList.forEach((cur) => {
          if (!ids?.includes(cur.id)) {
            res?.push(cur);
          }
        });
        return res;
      };
      const chanceDataList = chancePoint();
      const storeDataList = storeData();
      // 去掉已落位的机会点（此时已经存在对应的门店）
      const removeRepetitionChancePoint = () => {
        const ids = storeDataList?.map((item) => item.chancePointId);
        return chanceDataList?.reduce((acc, item) => {
          if (!ids?.includes(item.id)) {
            acc?.push(item);
          }
          return acc;
        }, []);
      };
      setClusterList(clusterList);
      setShopList(storeDataList);
      setChancePoint(removeRepetitionChancePoint());
    },

    handleProtectRadius(lnglat?:any) {
      const center = lnglat || centerMarkerRef?.current?.getPosition();
      if (!center) return;
      if (!centerCircleMarkerRef.current) {
        centerCircleMarkerRef.current = new window.AMap.Circle({
          center: center, // 圆心位置
          radius: protectRadius, // 半径
          strokeColor: '#006aff', // 设置边框颜色
          fillColor: '#006aff', // 设置填充颜色
          fillOpacity: 0.1,
          strokeStyle: 'dashed',
          bubble: true,
        });
        amapIns.add(centerCircleMarkerRef.current);
      } else {
        centerCircleMarkerRef.current.setCenter(center);
        centerCircleMarkerRef.current.setRadius(protectRadius);
      }
    }
  });

  const handleClickPosition = () => {
    if (!isNotEmpty(lng) || !isNotEmpty(lat)) return;

    if (amapIns && centerMarkerRef.current) {
      amapIns.setFitView(
        centerMarkerRef.current,
        false, // 动画过渡到制定位置
        [60, 60, 60, 60], // 周围边距，上、下、左、右
        ZoomLevel, // 最大 zoom 级别
      );
      // amapIns.setZoomAndCenter(TargetZoom, [+lng, +lat], false, 200);
    }
  };

  useEffect(() => {
    if (!amapIns) return;
    handleProtectRadius();
  }, [protectRadius]);


  return (
    <div className={cs(styles.circleMapCon, mapContainerAddress)}>
      <AMap
        loaded={loadedMapHandle}
        plugins={[
          'AMap.Geocoder',
          'AMap.AutoComplete',
        ]}
        mapOpts={{
          zoom: ZoomLevel,
          zooms: [10, 20],
          mapStyle: `amap://styles/${mapConfig?.bg || MapThemeEnum.NORMAL}`
        }}
      />
      <MapOfCIrcleAddress
        amapIns={amapIns}
        clusterList={clusterList}
        shopList={shopList}
        chancePoint={chancePoint}
      />
      <div className={styles.toolBottomBar} onClick={handleClickPosition}>
        <IconFont
          iconHref='iconic_mendiangengzhong'
          className='c-666'
        />
      </div>
    </div>
  );
};

export default AddressClusterMap;
